import { isFeatureFlagEnabled } from '@ipfabric/ui-components/utils/featureFlags';
import type { FeatureFlagName } from '@ipfabric/utils/featureFlag';
import type { LicenseAdditionalFeatureName } from '@ipfabric/utils/license/licenseSchema';
import type { FC, ReactNode } from 'react';
import { useLicenseFeatureAvailability } from 'utils/hooks/useLicenseFeatureAvailability';
import type { LicenseFeatureAvailability } from 'utils/license/licenseFeatures';
import { licenseFeatureDefinitions } from 'utils/license/licenseFeatures';

export type PageAccessRule =
  | { kind: 'feature-flag'; flag: FeatureFlagName }
  | { kind: 'license-feature'; licenseFeature: LicenseAdditionalFeatureName }
  | {
      kind: 'feature-and-license';
      flag: FeatureFlagName;
      licenseFeature: LicenseAdditionalFeatureName;
    };

export type PageAccess = 'hidden' | 'licensed' | 'unlicensed';

export const pageAccessRuleFromOasNode = (node: {
  'x-required-feature-flag'?: string;
  'x-require-license-feature'?: LicenseAdditionalFeatureName;
}): PageAccessRule | undefined => {
  const flag = node['x-required-feature-flag'] as FeatureFlagName | undefined;
  const licenseFeature = node['x-require-license-feature'];

  if (flag !== undefined && licenseFeature !== undefined) {
    return { kind: 'feature-and-license', flag, licenseFeature };
  }
  if (flag !== undefined) {
    return { kind: 'feature-flag', flag };
  }
  if (licenseFeature !== undefined) {
    return { kind: 'license-feature', licenseFeature };
  }
  return undefined;
};

/** Whether a page should be omitted from routes/menu based on feature flags only. */
export const isPageStructurallyHidden = (
  rule: PageAccessRule | undefined,
): boolean => {
  if (rule === undefined || rule.kind === 'license-feature') {
    return false;
  }
  return !isFeatureFlagEnabled(rule.flag);
};

export const evaluatePageAccess = (
  rule: PageAccessRule | undefined,
  licenseAvailability: LicenseFeatureAvailability,
): PageAccess => {
  if (rule === undefined) {
    return 'licensed';
  }

  if (rule.kind === 'feature-flag') {
    return isFeatureFlagEnabled(rule.flag) ? 'licensed' : 'hidden';
  }
  if (rule.kind === 'license-feature') {
    return licenseAvailability[rule.licenseFeature] ? 'licensed' : 'unlicensed';
  }
  if (!isFeatureFlagEnabled(rule.flag)) {
    return 'hidden';
  }

  return licenseAvailability[rule.licenseFeature] ? 'licensed' : 'unlicensed';
};

type LicenseGatedContentProps = {
  feature: LicenseAdditionalFeatureName;
  children: ReactNode;
};

export const LicenseGatedContent: FC<LicenseGatedContentProps> = ({
  feature,
  children,
}) => {
  const availability = useLicenseFeatureAvailability();

  if (!availability[feature]) {
    return (
      <div className='p flex flex-col flex-1'>
        {licenseFeatureDefinitions[feature].unlicensedContent}
      </div>
    );
  }

  return children;
};
